import { PcbNodeTypes, } from "@buildwithflux/models";
import { Records } from "@buildwithflux/shared";
import { Box3, BoxGeometry, Mesh, Vector3 } from "three";
import { cache } from "../../../cache";
import { ElementHelper } from "../../../elements";
import { calcMaxKeepoutForNode } from "../../../fills/keepOutHelpers";
import { MissingChildUidError } from "../../../flux_error";
import { getChildrenOfType, getDescendantsOfType, getTopLevelLayoutNonElementDescendantsPads, PcbNodeHelper, } from "../../../nodes";
import { getExistingNodeId } from "../helpers";
import { computeNodePositions } from "./PcbLayoutAutoInitialPositioning";
/**
 * This class was extracted from PcbLayoutEngine. It is just intended to
 * encapsulate the logic for getting the auto positions.
 *
 * According to original author Matthias: No tests exist, but it’s fairly
 * straightforward to test manually. Just drop a bunch of parts with footprint
 * on schematic and then switch to PCB. They should all be arranged without
 * intersecting each other. And it should roughly resemble positions/clusters
 * from the schematic…with slight gravity towards the center of the board.
 *
 * When should the autopositioning be reapplied? When a element nodes position
 * is set to “auto” and then:
 *  1. a element nodes bounding box changes
 *  2. A element node changes position
 *  3. A elements schematic position changes
 *  4. Element nodes get removed or added
 */
export class PcbAutoInitialPositionCalcs {
    elements;
    constructor(elements) {
        this.elements = elements;
    }
    autoInitialPositionLayout(pcbLayoutNodes, topLevelLayoutNode, logger, elementsMap = this.elements) {
        const computedNodePositions = [];
        if (topLevelLayoutNode.type === PcbNodeTypes.layout && topLevelLayoutNode.bakedRules.active) {
            const layoutElements = getDescendantsOfType(pcbLayoutNodes, topLevelLayoutNode, PcbNodeTypes.element, undefined, false);
            const topLevelLayoutPads = getTopLevelLayoutNonElementDescendantsPads(pcbLayoutNodes, topLevelLayoutNode.uid, layoutElements);
            for (const element of layoutElements) {
                const subLayouts = getChildrenOfType(pcbLayoutNodes, element.uid, PcbNodeTypes.layout, logger);
                for (const layout of subLayouts) {
                    const subElementUid = getExistingNodeId(element.uid);
                    const subElementsMap = elementsMap[subElementUid]?.part_version_data_cache?.extracted_element_cache;
                    if (subElementsMap) {
                        const nodePositions = this.autoInitialPositionLayout(pcbLayoutNodes, layout, logger, subElementsMap);
                        computedNodePositions.push(...nodePositions);
                    }
                }
            }
            if (Object.values(layoutElements).some(PcbNodeHelper.isAutoPositioned)) {
                const positions = this.autoPositionLayoutElements(pcbLayoutNodes, topLevelLayoutNode, layoutElements, topLevelLayoutPads, elementsMap, logger);
                computedNodePositions.push(...positions);
            }
        }
        return computedNodePositions;
    }
    // NOTE: this function can take >100ms on a 1000 node doc, thanks to Immer :(
    autoPositionLayoutElements(pcbLayoutNodes, layoutNode, layoutElements, layoutPads, elementsMap, logger) {
        const layoutElementBoxes = layoutElements
            .filter((elementNode) => {
            const subLayoutElementId = getExistingNodeId(elementNode.uid);
            // The second check ensures that we do not filter out sublayout elements
            // when we calculate the autopositioning for sublayouts.
            return elementNode.uid in elementsMap || (subLayoutElementId && subLayoutElementId in elementsMap);
        })
            .map((elementNode) => {
            const elementData = elementsMap[elementNode.uid] ?? elementsMap[getExistingNodeId(elementNode.uid)];
            const size = this.getNodeBounds(pcbLayoutNodes, elementNode, logger);
            const schematicPosition = elementData && !ElementHelper.isExtractedElement(elementData)
                ? Records.firstValue(ElementHelper.getSymbolPlacements(elementData)) ?? { x: 0, y: 0 }
                : { x: 0, y: 0 };
            return {
                uid: elementNode.uid,
                size,
                schematicPosition,
                pcbPosition: elementNode.bakedRules.position,
                // The user can set a keepOut value for elements, but also a bigger
                // one for vias/pads/traces. Since we don't want to violate the other
                // rules, we take the max value to be safe.
                // TODO: Revisit this to consider more specific spacing
                margin: calcMaxKeepoutForNode(elementNode),
            };
        });
        const layoutPadBoxes = layoutPads.map((node) => ({
            uid: node.uid,
            size: this.getNodeBounds(pcbLayoutNodes, node, logger),
            schematicPosition: { x: 0, y: 0 },
            pcbPosition: node.bakedRules.position,
            margin: 0,
        }));
        const config = {
            startIterations: 10, // see https://github.com/buildwithflux/flux-app/pull/4803/
            layoutSize: layoutNode?.bakedRules.size,
            layoutShape: layoutNode?.bakedRules.boardShape,
            gravity: layoutNode?.bakedRules.autoLayoutGravity,
            boxes: [...layoutElementBoxes, ...layoutPadBoxes],
        };
        // NOTE: this cache is not working from the first load because of
        // floating point or other issues
        return cache(() => {
            return computeNodePositions(config);
        }, "computeNodePositions", [config]);
    }
    getNodeBounds(pcbLayoutNodes, node, logger, ignoreNodeTypes = []) {
        const nodeMeshTree = this.buildMeshTree(pcbLayoutNodes, node, logger, ignoreNodeTypes);
        const bbox = new Box3().setFromObject(nodeMeshTree);
        const targetVector = new Vector3();
        bbox.getSize(targetVector);
        return {
            x: targetVector.x,
            y: targetVector.y,
            z: targetVector.z,
        };
    }
    // TODO: Flagging this method - from Profiler, this method doesn't seem to scale well: with 200+ parts, it takes ~50ms
    // to run this method, compared to ~270ms to run `autoPositionRootLayoutElements`
    buildMeshTree(pcbLayoutNodes, node, logger, ignoreNodeTypes = []) {
        const mesh = new Mesh();
        if (node.bakedRules) {
            if (ignoreNodeTypes.length === 0 || !ignoreNodeTypes.includes(node.type)) {
                if ("size" in node.bakedRules && node.bakedRules.size) {
                    const box = new BoxGeometry(node.bakedRules.size.x, node.bakedRules.size.y, node.bakedRules.size.z);
                    mesh.geometry = box;
                }
                if ("position" in node.bakedRules && node.bakedRules.position) {
                    mesh.position.set(node.bakedRules.position.x, node.bakedRules.position.y, node.bakedRules.position.z);
                }
                if ("scale" in node.bakedRules && node.bakedRules.scale) {
                    mesh.scale.set(node.bakedRules.scale.x, node.bakedRules.scale.y, node.bakedRules.scale.z);
                }
                if ("rotation" in node.bakedRules && node.bakedRules.rotation) {
                    mesh.rotation.set(node.bakedRules.rotation.x, node.bakedRules.rotation.y, node.bakedRules.rotation.z);
                }
            }
        }
        // NOTE: We play safe here to fallback childUids to empty, but want to know
        // that childUids is missing, so that we can investigate and take actions from here
        if (!node.childUids) {
            logger.captureError(new MissingChildUidError(`node ${node.uid} with type ${node.type} in buildMeshTree`));
        }
        const childUids = node.childUids || [];
        // NOTE: for loop to guarantee order
        for (const childUid of childUids) {
            const childNode = pcbLayoutNodes[childUid];
            if (childNode) {
                const childGroup = this.buildMeshTree(pcbLayoutNodes, childNode, logger);
                mesh.add(childGroup);
            }
        }
        return mesh;
    }
}
//# sourceMappingURL=PcbAutoInitialPositionCalcs.js.map